import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Edit3, X } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MODULE_OPTIONS = [
  { key: "dashboard", label: "Dashboard", always: true },
  { key: "settings", label: "Configuración / Home" },
  { key: "appearance", label: "Apariencia" },
  { key: "about", label: "Quiénes Somos" },
  { key: "services", label: "Servicios" },
  { key: "talents", label: "Talentos" },
  { key: "products", label: "Productos" },
  { key: "clients", label: "Clientes" },
  { key: "proposals", label: "Comercial / Propuestas" },
  { key: "leads", label: "Leads" },
  { key: "media", label: "Medios" },
];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/users").then(setUsers).catch((e) => setError(formatErr(e.response?.data?.detail) || "Error"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError("");
    // Ensure dashboard always included for editors
    const modules = editing.role === "super_admin"
      ? []
      : Array.from(new Set([...(editing.allowed_modules || []), "dashboard"]));
    try {
      if (editing.id && users.find((u) => u.id === editing.id)) {
        const body = { name: editing.name, role: editing.role, active: editing.active, allowed_modules: modules };
        if (editing.password) body.password = editing.password;
        await api.put(`/admin/users/${editing.id}`, body);
      } else {
        await api.post("/admin/users", {
          email: editing.email, name: editing.name || "", password: editing.password,
          role: editing.role || "editor", active: editing.active !== false,
          allowed_modules: modules,
        });
      }
      setEditing(null); load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || "Error al guardar");
    }
  };

  const toggleModule = (key) => {
    const curr = new Set(editing.allowed_modules || []);
    if (curr.has(key)) curr.delete(key); else curr.add(key);
    setEditing({ ...editing, allowed_modules: Array.from(curr) });
  };

  const presetPreset = (preset) => {
    const map = {
      comercial: ["proposals", "leads"],
      contenido: ["services", "talents", "products", "media"],
      editor_full: MODULE_OPTIONS.filter((m) => !m.always).map((m) => m.key),
    };
    setEditing({ ...editing, allowed_modules: map[preset] || [] });
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try { await api.delete(`/admin/users/${id}`); load(); }
    catch (e) { setError(formatErr(e.response?.data?.detail)); }
  };

  if (me?.role !== "super_admin") {
    return <div data-testid="admin-users"><h1 className="font-display text-4xl font-black tracking-tighter mb-2">Usuarios</h1><p className="text-muted-foreground">Solo super_admin puede gestionar usuarios.</p></div>;
  }

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Usuarios admin</h1>
          <p className="text-sm text-muted-foreground">Total: {users.length}</p>
        </div>
        <button onClick={() => setEditing({ email: "", name: "", role: "editor", active: true, password: "", allowed_modules: [] })} data-testid="new-user" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nuevo usuario
        </button>
      </div>

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6" data-testid="user-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id ? "Editar usuario" : "Nuevo usuario"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Email" type="email" value={editing.email} disabled={!!editing.id} onChange={(v) => setEditing({ ...editing, email: v })} />
            <Field label="Nombre" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            <Field label={editing.id ? "Nueva contraseña (opcional)" : "Contraseña"} type="password" value={editing.password || ""} onChange={(v) => setEditing({ ...editing, password: v })} />
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Rol</label>
              <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
                <option value="editor">Editor</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <label className="flex items-center gap-2 h-10">
              <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-primary" />
              <span className="text-sm">Activo</span>
            </label>
          </div>

          {editing.role === "editor" && (
            <div className="mt-6 p-4 border border-border rounded-sm bg-secondary/20">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h4 className="font-display font-bold text-sm">Módulos a los que tiene acceso</h4>
                  <p className="text-xs text-muted-foreground">El editor solo verá los módulos marcados. Dashboard y Mi cuenta se incluyen siempre.</p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <button onClick={() => presetPreset("comercial")} type="button" className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold border border-border rounded-sm hover:border-primary">Preset: Comercial</button>
                  <button onClick={() => presetPreset("contenido")} type="button" className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold border border-border rounded-sm hover:border-primary">Preset: Contenido</button>
                  <button onClick={() => presetPreset("editor_full")} type="button" className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold border border-border rounded-sm hover:border-primary">Todos</button>
                  <button onClick={() => setEditing({ ...editing, allowed_modules: [] })} type="button" className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold border border-border rounded-sm hover:border-primary">Ninguno</button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {MODULE_OPTIONS.filter((m) => !m.always).map((m) => {
                  const checked = (editing.allowed_modules || []).includes(m.key);
                  return (
                    <label key={m.key} className={`flex items-center gap-2 px-3 py-2 border rounded-sm cursor-pointer transition-colors ${checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}>
                      <input type="checkbox" data-testid={`module-${m.key}`} checked={checked} onChange={() => toggleModule(m.key)} className="accent-primary" />
                      <span className="text-sm">{m.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-destructive mt-3">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={save} data-testid="save-user" className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Nombre</th><th className="text-left p-3">Rol</th><th className="text-left p-3">Módulos</th><th className="text-left p-3">Activo</th><th className="text-right p-3">Acciones</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-secondary/30">
                <td className="p-3 font-semibold">{u.email}</td>
                <td className="p-3">{u.name || "—"}</td>
                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${u.role === "super_admin" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{u.role}</span></td>
                <td className="p-3 text-xs text-muted-foreground max-w-xs">
                  {u.role === "super_admin" ? <span className="font-semibold text-foreground">Todos</span>
                    : (u.allowed_modules || []).filter((m) => m !== "dashboard").length === 0
                      ? <span className="italic">Ninguno</span>
                      : (u.allowed_modules || []).filter((m) => m !== "dashboard").join(", ")}
                </td>
                <td className="p-3">{u.active ? "Sí" : "No"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing({ ...u, password: "" })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Edit3 className="w-3 h-3" /> Editar</button>
                  {u.id !== me.id && <button onClick={() => del(u.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /> Eliminar</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, disabled }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{label}</label>
      <input type={type} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none disabled:opacity-50" />
    </div>
  );
}
